import { useCallback, useMemo } from 'react';
import { useActiveProject } from '../../core/contexts/ProjectContext';
import { useTenant } from '../../core/contexts/TenantContext';
import { can, type Permission } from '../../core/services/permissions';
import type { ProjectRole } from '../../core/types/data-models';

/**
 * The caller's capabilities on the active project.
 *
 * UI gating only — every one of these is enforced server-side. The point is to
 * avoid rendering buttons that would 403, so a VIEWER sees a fully rendered
 * document with no edit affordances rather than edit buttons that fail on click.
 */
export function usePermissions() {
  const { activeProject } = useActiveProject();
  const { isGuest } = useTenant();

  // A project opened from the caller's own workspace carries no explicit role —
  // membership of the owning workspace makes them OWNER of everything in it.
  const role: ProjectRole | undefined = useMemo(() => {
    if (!activeProject) return undefined;
    if (activeProject.role) return activeProject.role;
    return isGuest ? undefined : 'OWNER';
  }, [activeProject, isGuest]);

  const check = useCallback((permission: Permission) => can(role, permission), [role]);

  return useMemo(
    () => ({
      role,
      can: check,
      canView: check('project:view'),
      canEdit: check('project:edit'),
      canGenerate: check('document:generate'),
      canComment: check('comment:create'),
      canResolveComments: check('comment:resolve'),
      canManageMembers: check('project:manage_members'),
      canUseAssistantTools: check('assistant:write'),
      // Deleting is additionally restricted to members of the owning workspace:
      // an invited OWNER may edit and invite, never destroy the host's project.
      canDelete: check('project:delete') && !isGuest,
    }),
    [role, check, isGuest],
  );
}
